import axios from "axios";
import firebase from "config/firebase";
import moment from "moment";
import { toast } from "react-toastify";
import { RepositoryFactory } from "repositroy/RepositoryFactory";
import { revertBidEmailTemplate } from "variables/emailTemplates";
import { reservedBidEmailTemplate } from "variables/emailTemplates";
import { rejectBidEmailTemplate } from "variables/emailTemplates";
import { approveBidEmailTemplate } from "variables/emailTemplates";
import { STATUS_ORDER } from "variables/statusOrder";

export const fetchVendors =
  (lastVisibleDoc = null, activeTab, onSuccess = () => {}) =>
  async (dispatch) => {
    dispatch({ type: "FETCH_VENDORS_PENDING" });
    try {
      const limit = 10;
      let query = firebase
        .firestore()
        .collection("users")
        .where("role", "==", "vendor")
        .orderBy("createdAt", "desc")
        .limit(limit);

      if (activeTab === "vendorRequests") {
        query = query.where("status", "==", "pending");
      } else if (activeTab === "activeVendors") {
        query = query.where("status", "==", "approved");
      }

      if (lastVisibleDoc) {
        query = query.startAfter(lastVisibleDoc);
      }

      const querySnapshot = await query.get();
      const vendors = [];
      let lastDoc = null;

      querySnapshot.forEach((doc) => {
        vendors.push({ id: doc.id, ...doc.data() });
      });

      const hasMore = querySnapshot.size >= limit;
      lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];

      if (lastVisibleDoc) {
        dispatch({
          type: "FETCH_VENDORS_SUCCESS",
          payload: { vendors, lastDoc, hasMore },
        });
      } else {
        dispatch({
          type: "FETCH_VENDORS_SUCCESS_EMPTY",
          payload: { vendors, lastDoc, hasMore },
        });
      }
      onSuccess();
    } catch (error) {
      console.error("Error fetching vendors: ", error);
      dispatch({ type: "FETCH_VENDORS_REJECTED" });
    }
  };
export const fetchVendorsForBids =
  (activeTab, onSuccess = () => {}) =>
  async (dispatch) => {
    try {
      let query = firebase
        .firestore()
        .collection("users")
        .where("role", "==", "vendor")
        .orderBy("createdAt", "desc");

      if (activeTab === "vendorRequests") {
        query = query.where("status", "==", "pending");
      } else if (activeTab === "activeVendors") {
        query = query.where("status", "==", "approved");
      }

      const querySnapshot = await query.get();
      const vendorsForBids = [];

      querySnapshot.forEach((doc) => {
        vendorsForBids.push({ id: doc.id, ...doc.data() });
      });

      dispatch({
        type: "FETCH_VENDORS_FOR_BIDS",
        payload: vendorsForBids,
      });

      onSuccess();
    } catch (error) {
      console.error("Error fetching vendors: ", error);
      dispatch({ type: "FETCH_VENDORS_REJECTED" });
    }
  };

export const fetchVendorById =
  ({ vendorId, onError = () => {} }) =>
  async (dispatch) => {
    dispatch({ type: "FETCH_VENDOR_BY_ID_PENDING" });

    try {
      const vendorRef = firebase.firestore().collection("users").doc(vendorId);
      const doc = await vendorRef.get();

      if (doc.exists) {
        const vendor = { id: doc.id, ...doc.data() };
        dispatch({ type: "FETCH_VENDOR_BY_ID_SUCCESS", payload: vendor });
      } else {
        onError();
        toast.error("Vendor not found");
        dispatch({ type: "FETCH_VENDOR_BY_ID_REJECTED" });
      }
    } catch (error) {
      onError();
      toast.error(error.message || "An unknown error occured");
      dispatch({ type: "FETCH_VENDOR_BY_ID_REJECTED" });
    }
  };

export const approveVendor =
  ({ vendorId, onSuccess = () => {} }) =>
  async (dispatch) => {
    dispatch({ type: "VENDOR_ACTION_PENDING" });
    try {
      await firebase.firestore().collection("users").doc(vendorId).update({
        status: "approved",
        updatedAt: new Date(),
      });
      toast.success("Vendor Approved Successfully!");
      onSuccess();
      dispatch({
        type: "VENDOR_ACTION_SUCCESS",
        payload: { vendorId, status: "approved" },
      });
    } catch (error) {
      toast.error(error.message || "An unknown error occured");
      dispatch({ type: "VENDOR_ACTION_REJECTED" });
    }
  };

export const rejectVendor =
  ({ vendorId, onSuccess = () => {} }) =>
  async (dispatch) => {
    dispatch({ type: "VENDOR_ACTION_PENDING" });
    try {
      await firebase.firestore().collection("users").doc(vendorId).delete();
      toast.success("Vendor Rejected Successfully!");
      onSuccess();
      dispatch({ type: "REJECT_VENDOR_SUCCESS", payload: vendorId });
    } catch (error) {
      toast.error(error.message || "An unknown error occured");
      dispatch({ type: "VENDOR_ACTION_REJECTED" });
    }
  };

// ====================== BID ACTIONS =======================

export const fetchBids =
  (lastVisibleDoc = null, activeTab, category = null, filter = null) =>
  async (dispatch) => {
    dispatch({ type: "FETCH_BIDS_PENDING" });
    try {
      const limitCount = 10;
      let query = firebase
        .firestore()
        .collection("vendor_bids")
        .limit(limitCount);

      if (activeTab === "activeBids") {
        query = query.where("status", "==", "active");
      } else if (activeTab === "acceptedBids") {
        query = query.where("status", "==", "accepted");
      } else {
        if (filter) {
          if (filter?.bid) {
            query = query.orderBy("bid", "desc");
            const bidRange = filter?.bid;
            switch (bidRange) {
              case "Less than 50":
                query = query.where("bid", "<", 50);
                break;
              case "51 - 100":
                query = query.where("bid", ">=", 51).where("bid", "<=", 100);
                break;
              case "100 - 200":
                query = query.where("bid", ">=", 100).where("bid", "<=", 200);
                break;
              case "200 - 300":
                query = query.where("bid", ">=", 200).where("bid", "<=", 300);
                break;
              case "Above 300":
                query = query.where("bid", ">", 300);
                break;
              default:
                break;
            }
          }
          if (filter?.status) {
            query = query.where("status", "==", filter?.status);
          }
        }
        if (category) {
          query = query.where("category", "==", category);
        }
        if (!filter?.status) {
          query = query.where("status", "in", [
            "pending",
            "payment_pending",
            "resolved",
            "issue",
            "reserved",
          ]);
        }

        if (!filter?.bid) {
          query = query.orderBy("statusOrder", "asc");
        }
      }

      if (lastVisibleDoc) {
        query = query.startAfter(lastVisibleDoc);
      }

      const querySnapshot = await query.get();
      const bids = [];
      let lastDoc = null;

      querySnapshot.forEach((doc) => {
        bids.push({ id: doc.id, ...doc.data() });
      });

      const hasMore = querySnapshot.size >= limitCount;
      lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];

      if (lastVisibleDoc) {
        dispatch({
          type: "FETCH_BIDS_SUCCESS",
          payload: { bids, lastDoc, hasMore },
        });
      } else {
        dispatch({
          type: "FETCH_BIDS_SUCCESS_EMPTY",
          payload: { bids, lastDoc, hasMore },
        });
      }
    } catch (error) {
      console.error("Error fetching bids: ", error);
      dispatch({ type: "FETCH_BIDS_REJECTED" });
    }
  };

export const fetchBidRequestsStats = () => async (dispatch) => {
  dispatch({ type: "FETCH_BIDS_STATS_PENDING" });
  try {
    const bidRepository = RepositoryFactory.get("bid");
    const response = await bidRepository.getBidRequestsStats();
    const { data } = response;

    if (data?.success) {
      dispatch({ type: "FETCH_BIDS_STATS_SUCCESS", payload: data?.data });
    } else {
      toast.error(data?.message || "An unknown error occurred");
      dispatch({ type: "FETCH_BIDS_STATS_REJECTED" });
    }
  } catch (error) {
    toast.error(error.message || "An unknown error occurred");
    dispatch({ type: "FETCH_BIDS_STATS_REJECTED" });
  }
};

export const approveBid =
  ({ bidId, status, payload, onSuccess = () => {} }) =>
  async (dispatch) => {
    dispatch({ type: "BID_ACTION_PENDING" });

    let firestoreUpdateSuccessful = false;
    let emailSentSuccessful = false;

    try {
      const statusOrder = STATUS_ORDER[status];
      await firebase.firestore().collection("vendor_bids").doc(bidId).update({
        status,
        statusOrder,
        updatedAt: new Date(),
      });
      firestoreUpdateSuccessful = true;

      let emailData;
      if (status === "payment_pending") {
        emailData = approveBidEmailTemplate(
          payload?.username,
          payload?.category
        );
      } else if (status === "reserved") {
        emailData = reservedBidEmailTemplate(
          payload?.username,
          payload?.category
        );
      }
      const options = {
        to: payload?.email,
        subject: `Bid ${
          status === "payment_pending" ? "Approved" : "Reserved"
        } for FHP Platform`,
        text: emailData,
      };

      await axios.post(
        `${process.env.REACT_APP_FIREBASE_BASE_DOMAIN}/sendUserEmail`,
        options
      );
      emailSentSuccessful = true;

      if (firestoreUpdateSuccessful && emailSentSuccessful) {
        toast.success("Bid Approved and Email Sent Successfully!");
        onSuccess();
        dispatch({
          type: "BID_ACTION_SUCCESS",
          payload: { bidId, status, statusOrder },
        });
      }
    } catch (error) {
      if (firestoreUpdateSuccessful && !emailSentSuccessful) {
        await firebase.firestore().collection("vendor_bids").doc(bidId).update({
          status: "pending",
          statusOrder: STATUS_ORDER["pending"],
          updatedAt: new Date(),
        });
      }

      toast.error(error.message || "An unknown error occurred");
      dispatch({ type: "BID_ACTION_REJECTED" });
    }
  };

export const revertBid =
  ({ bidId, currentStatus, payload, onSuccess = () => {} }) =>
  async (dispatch) => {
    dispatch({ type: "BID_ACTION_PENDING" });

    let firestoreUpdateSuccessful = false;
    let emailSentSuccessful = false;

    try {
      const statusOrder = STATUS_ORDER["pending"];
      await firebase.firestore().collection("vendor_bids").doc(bidId).update({
        status: "pending",
        statusOrder,
        updatedAt: new Date(),
      });
      firestoreUpdateSuccessful = true;

      const emailData = revertBidEmailTemplate(
        payload?.username,
        payload?.category
      );
      const options = {
        to: payload?.email,
        subject: "Bid Reverted for FHP Platform",
        text: emailData,
      };

      await axios.post(
        `${process.env.REACT_APP_FIREBASE_BASE_DOMAIN}/sendUserEmail`,
        options
      );
      emailSentSuccessful = true;

      if (firestoreUpdateSuccessful && emailSentSuccessful) {
        toast.success("Bid Reverted and Email Sent Successfully!");
        onSuccess();
        dispatch({
          type: "BID_ACTION_SUCCESS",
          payload: { bidId, status: "pending", statusOrder },
        });
      }
    } catch (error) {
      if (firestoreUpdateSuccessful && !emailSentSuccessful) {
        await firebase.firestore().collection("vendor_bids").doc(bidId).update({
          status: currentStatus,
          statusOrder: STATUS_ORDER[currentStatus],
          updatedAt: new Date(),
        });
      }

      toast.error(error.message || "An unknown error occurred");
      dispatch({ type: "BID_ACTION_REJECTED" });
    }
  };

export const rejectBid =
  ({ bidId, payload, onSuccess = () => {} }) =>
  async (dispatch) => {
    dispatch({ type: "BID_ACTION_PENDING" });

    let firestoreDeleteSuccessful = false;
    let emailSentSuccessful = false;
    let bidData;

    try {
      const bidDoc = await firebase
        .firestore()
        .collection("vendor_bids")
        .doc(bidId)
        .get();
      bidData = bidDoc.data();

      await firebase.firestore().collection("vendor_bids").doc(bidId).delete();
      firestoreDeleteSuccessful = true;

      const emailData = rejectBidEmailTemplate(
        payload?.username,
        payload?.category
      );
      const options = {
        to: payload?.email,
        subject: "Bid Rejection Notification",
        text: emailData,
      };

      await axios.post(
        `${process.env.REACT_APP_FIREBASE_BASE_DOMAIN}/sendUserEmail`,
        options
      );
      emailSentSuccessful = true;

      if (firestoreDeleteSuccessful && emailSentSuccessful) {
        toast.success("Bid Rejected and Email Sent Successfully!");
        onSuccess();
        dispatch({ type: "REJECT_BID_SUCCESS", payload: bidId });
      }
    } catch (error) {
      if (firestoreDeleteSuccessful && !emailSentSuccessful && bidData) {
        await firebase
          .firestore()
          .collection("vendor_bids")
          .doc(bidId)
          .set(bidData);
      }

      toast.error(error.message || "An unknown error occurred");
      dispatch({ type: "BID_ACTION_REJECTED" });
    }
  };

export const reportBidIssue =
  ({ reason, bidId, onSuccess = () => {} }) =>
  async (dispatch) => {
    dispatch({ type: "BID_ACTION_PENDING" });
    try {
      const statusOrder = STATUS_ORDER["issue"];
      await firebase.firestore().collection("vendor_bids").doc(bidId).update({
        status: "issue",
        statusOrder,
        issue: reason,
        updatedAt: new Date(),
      });
      toast.success("Bid Rejected Successfully!");
      onSuccess();
      dispatch({
        type: "BID_ACTION_SUCCESS",
        payload: { bidId, status: "issue", statusOrder, reason },
      });
    } catch (error) {
      toast.error(error.message || "An unknown error occured");
      dispatch({ type: "BID_ACTION_REJECTED" });
    }
  };

export const getHistoryBids =
  (date, onComplete = () => {}) =>
  async (dispatch) => {
    const startOfMonth = moment(date, "YYYY-MM").startOf("month").toDate();
    const endOfMonth = moment(date, "YYYY-MM").endOf("month").toDate();
    const startTimestamp = firebase.firestore.Timestamp.fromDate(startOfMonth);
    const endTimestamp = firebase.firestore.Timestamp.fromDate(endOfMonth);
    firebase
      .firestore()
      .collection("vendor_bids")
      .where("status", "==", "in_active")
      .where("createdAt", ">=", startTimestamp)
      .where("createdAt", "<=", endTimestamp)
      .get()
      .then(async (query) => {
        let data = [];
        for await (let doc of query.docs) {
          data.push({ id: doc.id, ...doc.data() });
        }
        dispatch({
          type: "GET_BID_HISTORY",
          payload: data,
        });
        onComplete();
      });
  };

export const getSubstituteRequests =
  (onComplete = () => {}) =>
  async (dispatch) => {
    firebase
      .firestore()
      .collection("bid_substitute_request")
      .where("status", "==", "pending")
      .get()
      .then(async (query) => {
        let data = [];
        for await (let doc of query.docs) {
          data.push({ id: doc.id, ...doc.data() });
        }
        dispatch({
          type: "GET_SUBSTITUTE_REQUESTS",
          payload: data,
        });
        onComplete();
      });
  };

export const acceptSubstituteRequest =
  (id, onComplete = () => {}) =>
  async (dispatch) => {
    try {
      await firebase
        .firestore()
        .collection("bid_substitute_request")
        .doc(id)
        .get()
        .then(async (query) => {
          let { collectionId, createdA, reason, status, ...newData } =
            query.data();
          await firebase
            .firestore()
            .collection("vendor_bids")
            .doc(collectionId)
            .update({
              ...newData,
              updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
            });
          await firebase
            .firestore()
            .collection("bid_substitute_request")
            .doc(id)
            .delete();
          dispatch({
            type: "REJECTED_SUBSTITUTE_REQUEST",
            payload: id,
          });
          toast.success("Accepted Request");
          onComplete();
        });
    } catch (error) {
      toast.error(error.message, "Accepted Request Error");
      onComplete();
    }
  };

export const rejectSubstituteRequest =
  (id, onComplete = () => {}) =>
  async (dispatch) => {
    try {
      await firebase
        .firestore()
        .collection("bid_substitute_request")
        .doc(id)
        .update({
          status: "rejected",
        });
      dispatch({
        type: "REJECTED_SUBSTITUTE_REQUEST",
        payload: id,
      });
      toast.success("Rejected Request");
      onComplete();
    } catch (error) {
      toast.error(error.message, "Rejected Request Error");
      onComplete();
    }
  };
